import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Users, Calendar, Briefcase } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { ProfessionalCard } from '@/components/professionals/ProfessionalCard';
import { EventCard } from '@/components/events/EventCard';
import { JobCard } from '@/components/jobs/JobCard';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

type SearchType = 'all' | 'professionals' | 'events' | 'jobs';

const Search = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState<SearchType>('all');

  // Get language for localized fields
  const lang = (i18n.resolvedLanguage || i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';

  // Fetch professionals/companies
  const { data: professionalsData, isLoading: professionalsLoading } = useQuery({
    queryKey: ['search-professionals', query],
    queryFn: async () => {
      if (!query.trim()) return [];
      const { data } = await supabase.functions.invoke('search-professionals', {
        body: { q: query.trim(), limit: 12, name_city_only: true }
      });
      return data?.results || [];
    },
    enabled: !!query.trim()
  });

  // Fetch events
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['search-events', query],
    queryFn: async () => {
      if (!query.trim()) return [];
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'published')
        .gte('end_datetime', new Date().toISOString())
        .or(`title.ilike.%${query}%,location_city.ilike.%${query}%`)
        .order('start_datetime', { ascending: true })
        .limit(12);
      if (error) throw error;
      return data || [];
    },
    enabled: !!query.trim()
  });

  // Fetch jobs
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['search-jobs', query],
    queryFn: async () => {
      if (!query.trim()) return [];
      const { data, error } = await supabase
        .from('job_postings')
        .select(`
          *,
          companies:company_id (
            id, name, slug, logo_url
          )
        `)
        .eq('status', 'active')
        .gte('expires_at', new Date().toISOString())
        .or(`title.ilike.%${query}%,location_city.ilike.%${query}%`)
        .order('published_at', { ascending: false })
        .limit(12);
      if (error) throw error;
      return data || [];
    },
    enabled: !!query.trim()
  });

  const professionals = professionalsData || [];
  const events = eventsData || [];
  const jobs = jobsData || [];

  const isLoading = professionalsLoading || eventsLoading || jobsLoading;

  const counts = {
    all: professionals.length + events.length + jobs.length,
    professionals: professionals.length,
    events: events.length,
    jobs: jobs.length
  };

  const tabs: { key: SearchType; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: t('search.showAll'), icon: null },
    { key: 'professionals', label: t('search.showProfessionals'), icon: <Users className="w-4 h-4" /> },
    { key: 'events', label: t('search.showEvents'), icon: <Calendar className="w-4 h-4" /> },
    { key: 'jobs', label: t('search.showJobs'), icon: <Briefcase className="w-4 h-4" /> }
  ];

  const showProfessionals = activeTab === 'all' || activeTab === 'professionals';
  const showEvents = activeTab === 'all' || activeTab === 'events';
  const showJobs = activeTab === 'all' || activeTab === 'jobs';

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={t('search.pageTitle')}
        description={t('search.pageDescription')}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-8">
            <SEOBreadcrumb items={[{ label: t('search.pageTitle') }]} />
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-dark mb-4 flex items-center justify-center gap-3">
              {query ? t('search.resultsFor', { query }) : t('search.pageTitle')}
              {isLoading && <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />}
            </h1>
            {query && !isLoading && (
              <p className="font-body text-lg text-muted-foreground">
                {t('search.foundCount', { count: counts.all })}
              </p>
            )}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {tabs.map((tab) => (
              <BrutalButton
                key={tab.key}
                variant={activeTab === tab.key ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-2"
              >
                {tab.icon}
                {tab.label} ({counts[tab.key]})
              </BrutalButton>
            ))}
          </div>

          {/* No query message */}
          {!query && (
            <div className="text-center py-16 bg-card border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark">
              <p className="text-muted-foreground text-lg">{t('search.enterQuery')}</p>
            </div>
          )}

          {/* No results */}
          {query && !isLoading && counts.all === 0 && (
            <div className="text-center py-16 bg-card border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark">
              <p className="text-muted-foreground text-lg">{t('search.noResults')}</p>
            </div>
          )}

          {/* Results Grid */}
          {query && counts.all > 0 && (
            <div className="space-y-12">
              {/* Professionals Section */}
              {showProfessionals && professionals.length > 0 && (
                <section>
                  {activeTab === 'all' && (
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-2xl font-bold text-dark flex items-center gap-2">
                        <Users className="w-6 h-6" />
                        {t('search.showProfessionals')} ({professionals.length})
                      </h2>
                      <Link to={`/professionals?q=${encodeURIComponent(query)}`}>
                        <BrutalButton variant="outline" size="sm">
                          {t('search.viewAll')}
                        </BrutalButton>
                      </Link>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {professionals.map((professional: any) => (
                      <ProfessionalCard key={professional.id} data={professional} />
                    ))}
                  </div>
                </section>
              )}

              {/* Events Section */}
              {showEvents && events.length > 0 && (
                <section>
                  {activeTab === 'all' && (
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-2xl font-bold text-dark flex items-center gap-2">
                        <Calendar className="w-6 h-6" />
                        {t('search.showEvents')} ({events.length})
                      </h2>
                      <Link to={`/events?search=${encodeURIComponent(query)}`}>
                        <BrutalButton variant="outline" size="sm">
                          {t('search.viewAll')}
                        </BrutalButton>
                      </Link>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {events.map((event: any) => (
                      <EventCard key={event.id} data={event} />
                    ))}
                  </div>
                </section>
              )}

              {/* Jobs Section */}
              {showJobs && jobs.length > 0 && (
                <section>
                  {activeTab === 'all' && (
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-2xl font-bold text-dark flex items-center gap-2">
                        <Briefcase className="w-6 h-6" />
                        {t('search.showJobs')} ({jobs.length})
                      </h2>
                      <Link to={`/jobs?search=${encodeURIComponent(query)}`}>
                        <BrutalButton variant="outline" size="sm">
                          {t('search.viewAll')}
                        </BrutalButton>
                      </Link>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {jobs.map((job: any) => (
                      <Link key={job.id} to={`/jobs/${job.slug}`}>
                        <JobCard job={job} />
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* Loading Skeletons */}
          {isLoading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i} 
                  className="h-64 border-[3px] border-dark bg-muted animate-pulse shadow-[6px_6px_0px_0px] shadow-dark"
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Search;
